import { ColumnDef, Row } from '@tanstack/react-table';
import { ServerPaginatedTable } from '../../../components/Table/ServerPaginatedTable';
import { useSortedTable } from '../../../hooks/useSortedTable';
import { usePaginatedTable } from '../../../hooks/usePaginatedTable';
import { FC, useCallback, useEffect, useState } from 'react';
import format from 'date-fns/format';
import SearchBar from '../../../components/Table/SearchBar';
import { GraniteSelect, OptionType } from '../../../components/Select/Select';
import { useFilterForTable } from '../../../hooks/useFilterForTable';
import { Chip } from '../../../components/Chip/Chip';
import { useNavigate } from 'react-router-dom';
import Filters from 'components/Filters';
import { MultiValue, SingleValue } from 'react-select';
import { filterByDate } from 'screens/Resources';
import { dateToUTCWithoutMS } from 'shared/util/dateToUtcStrWithoutMS';
import { endOfDay } from 'date-fns';
import { getCookie, setCookie } from 'shared/util/util';
import { useQueryParams } from 'hooks/useQueryParams';
import { sortOptionsAlphabetically } from 'shared/util/sortOptionsAlphabetically';
import {
  OrderResponse,
  OrdersFilterSearchParams,
  OrdersMetadata,
} from 'api/accessexpressv3/schemas/OrdersSchemas';
import { deleteOrder, getOrdersIndex } from 'api/accessexpressv3/api';
import { EmptyOrdersIndex, EmptySearchResults } from './EmptyResults';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { useModal } from 'hooks/useModal';
import { DeleteOrderConfirmationDialog } from './DeleteOrderConfirmationDialog';
import { getQueryClient } from 'query-client';
import showToast from 'components/Toast/Toast';
import { useMutation } from 'react-query';

const QuoteItemStatus = ({
  status,
}: {
  status: OrdersFilterSearchParams['status'];
}) => {
  if (status === 'submitted')
    return (
      <>
        <span className="min-h-[10px] min-w-[10px] rounded-full bg-green-400" />
        <div>Order submitted</div>
      </>
    );
  if (status === 'draft')
    return (
      <>
        <span className="min-h-[10px] min-w-[10px] rounded-full bg-neutral-500" />
        <div>Draft</div>
      </>
    );
  return (
    <>
      <span className="min-h-[10px] min-w-[10px] rounded-full bg-green-400" />
      <div>{status}</div>
    </>
  );
};

export const OrdersIndex: FC<{
  status: OrdersFilterSearchParams['status'];
}> = ({ status }) => {
  const { getQueryParam, setQueryParam, resetQueryParams } = useQueryParams({
    search: {
      defaultValue: '',
      getParam: (value) => value,
      setParam: (value) => value,
    },
    requester: {
      defaultValue: [] as MultiValue<OptionType>,
      getParam: (value) =>
        value ? value.split(',').map((v) => ({ label: v, value: v })) : [],
      setParam: (value) => value.map(({ value }) => value).join(','),
    },
    date: {
      defaultValue: null as SingleValue<OptionType>,
      getParam: (value) => ({
        label: value,
        value: filterByDate.find((f) => f.label === value)?.value || '',
      }),
      setParam: (value) => (value ? value.label : ''),
    },
  });

  const [search, setSearch] = useState(getQueryParam('search'));
  const [pageSize, setPageSize] = useState<number>();
  const navigate = useNavigate();
  const [dateFilter, setDateFilter] = useState<SingleValue<OptionType>>(
    getQueryParam('date'),
  );

  const { sortingQueryParams, sortingState, onSortingChange } = useSortedTable({
    initialSorting: [{ id: 'created_at', desc: true }],
  });

  const {
    queryParamFilter: requesterFilter,
    clearFilter: clearRequesterFilter,
    ...requesterFilterProps
  } = useFilterForTable({
    queryParamKey: 'requester',
    defaultValue: getQueryParam('requester'),
    onAfterChange: (values) => setQueryParam('requester', values),
  });

  const handleDateFilterChange = useCallback(
    (value: SingleValue<OptionType>) => {
      setDateFilter(value);
      setQueryParam('date', value);
    },
    [setQueryParam],
  );

  const getOrdersIndexFn = useCallback(
    (args?: OrdersFilterSearchParams) => {
      return getOrdersIndex({
        ...(search ? { search } : {}),
        ...(dateFilter
          ? {
              start_date: dateFilter.value,
              end_date: dateToUTCWithoutMS(endOfDay(new Date())),
            }
          : {}),
        ...sortingQueryParams,
        ...args,
        ...requesterFilter,
        status,
      });
    },
    [dateFilter, search, sortingQueryParams, requesterFilter, status],
  );

  const {
    data: tablePageData,
    metadata,
    ...paginatedTableProps
  } = usePaginatedTable<OrderResponse, OrdersMetadata>(
    getOrdersIndexFn,
    {
      search,
      sortingQueryParams,
    },
    [
      'orders-index-table',
      sortingQueryParams,
      search,
      dateFilter,
      requesterFilter,
      status,
    ],
    {
      refetchOnMount: true,
    },
  );

  const clearAllFilters = useCallback(() => {
    handleDateFilterChange(null);
    clearRequesterFilter();
    resetQueryParams();
  }, [clearRequesterFilter, handleDateFilterChange, resetQueryParams]);

  const handleRowClick = (row: Row<OrderResponse>) => {
    if (status === 'draft')
      navigate(`/access-express/v3/${row.original.id}/order-services/access`);
    else navigate(`/access-express/v3/${row?.original?.id}`);
  };
  useEffect(() => {
    const savedSize = getCookie('paginationSizeAccessExpressV3');
    if (savedSize) {
      setPageSize(parseInt(savedSize, 10));
    }
  }, []);

  const paginationChanged = (page: number) => {
    setPageSize(page);
    setCookie('paginationSizeAccessExpress', page.toString(), 365);
  };

  const modalProps = useModal<OrderResponse>();
  const queryClient = getQueryClient();

  const { mutate: _deleteOrder, isLoading } = useMutation(
    (orderId: string) => deleteOrder(orderId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [
            'orders-index-table',
            sortingQueryParams,
            search,
            dateFilter,
            requesterFilter,
            status,
          ],
        });
        showToast.confirmation({
          message: 'Order has been successfully deleted',
        });
        modalProps.close();
      },
    },
  );

  const onDeleteOrderConfirmed = (order?: OrderResponse) => {
    if (order) _deleteOrder(order.id);
  };

  const getOrdersIndexTableDef = (): ColumnDef<OrderResponse>[] => {
    const columns: ColumnDef<OrderResponse>[] = [
      {
        header: 'Name',
        accessorKey: 'order_name',
      },
      {
        header: status === 'draft' ? 'Created by' : 'Submitted by',
        accessorKey: 'requester',
      },
      {
        header: 'Order #',
        accessorKey: 'order_number',
        cell: ({ getValue }) =>
          getValue() ? (
            <span className="h-6 rounded-lg bg-background-base-surface-3 px-2 text-xs font-bold leading-6">
              #{getValue() as string}
            </span>
          ) : (
            ''
          ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => {
          const status = row.original.status;
          return (
            <div className="flex items-center justify-center gap-2 text-sm">
              <QuoteItemStatus
                status={status as OrdersFilterSearchParams['status']}
              />
            </div>
          );
        },
        enableSorting: false,
      },
      {
        header: 'Last updated',
        accessorKey: 'updated_at',
        cell: ({ getValue }) => {
          const date = getValue() as string | undefined;

          if (!date) {
            return (
              <span className="text-sm text-content-base-subdued">N/A</span>
            );
          }

          const formattedDate = format(
            typeof date === 'string' ? new Date(date) : date,
            'MM/dd/yyyy',
          );
          return formattedDate;
        },
      },
      {
        header: status === 'draft' ? 'Date created' : 'Date ordered',
        accessorKey: 'created_at',
        cell: ({ getValue }) => {
          const date = getValue() as string | undefined;

          if (!date) {
            return (
              <span className="text-sm text-content-base-subdued">N/A</span>
            );
          }

          const formattedDate = format(
            typeof date === 'string' ? new Date(date) : date,
            'MM/dd/yyyy',
          );
          return formattedDate;
        },
      },
      ...(status === 'draft'
        ? [
            {
              header: 'Actions',
              cell: ({ row }: { row: Row<OrderResponse> }) => (
                <>
                  <GraniteButton
                    size="small"
                    className="mr-3"
                    variant="secondary"
                    onClick={(e) => {
                      e.stopPropagation();
                      const hasMobilityItems =
                        row.original.order_items.length > 0;
                      navigate(
                        `/access-express/v3/${row.original.id}/order-services/${
                          hasMobilityItems
                            ? 'mobility?type=SMART%2520PHONE'
                            : 'access'
                        }`,
                      );
                    }}
                  >
                    Resume draft
                  </GraniteButton>
                  <GraniteButton
                    size="small"
                    className="mr-1"
                    variant="destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      modalProps.openWithProps(row.original);
                    }}
                  >
                    Delete
                  </GraniteButton>
                </>
              ),
            },
          ]
        : []),
    ];

    return columns;
  };

  return (
    <>
      <div className="mb-4 flex w-full flex-wrap items-start justify-between gap-4">
        <div className="w-full sm:flex-1">
          <SearchBar
            placeholder="Search by name or order #"
            onSearch={(query) => {
              setSearch(query);
              setQueryParam('search', query);
            }}
            onQueryClear={() => {
              setSearch('');
              setQueryParam('search', '');
            }}
            clearAllValues={search === ''}
            defaultQuery={search}
          />
        </div>

        <Filters
          clearFilters={clearAllFilters}
          clearFilterClassName="col-span-1"
        >
          <GraniteSelect
            isMulti
            className="col-span-1"
            placeholder={
              status === 'draft' ? 'Filter by creator' : 'Filter by requester'
            }
            options={
              metadata && metadata.requesters
                ? sortOptionsAlphabetically(
                    metadata.requesters.map((option) => ({
                      label: option,
                      value: option,
                    })),
                  )
                : []
            }
            controlShouldRenderValue={false}
            isSearchable={false}
            {...requesterFilterProps}
          />
          <GraniteSelect
            options={filterByDate}
            isClearable={false}
            isSearchable={false}
            className="col-span-1"
            placeholder={
              status === 'draft'
                ? 'Filter by date submitted'
                : 'Filter by date ordered'
            }
            controlShouldRenderValue={false}
            onChange={handleDateFilterChange}
            value={dateFilter}
          />
        </Filters>
      </div>
      <div className="gap mb-12 flex gap-4">
        {requesterFilterProps.value.map((filter) => (
          <Chip
            key={filter.value}
            label={filter.label}
            className="mt-4"
            onDelete={() => {
              requesterFilterProps.onChange(
                requesterFilterProps.value.filter(
                  (v) => v.value !== filter.value,
                ),
              );
            }}
          />
        ))}
        {dateFilter?.value && (
          <Chip
            label={dateFilter.label}
            onDelete={() => handleDateFilterChange(null)}
            className="mt-4"
          />
        )}
      </div>
      <ServerPaginatedTable
        data={tablePageData as OrderResponse[]}
        columns={getOrdersIndexTableDef()}
        title="orders index table"
        sortingState={sortingState}
        onSortingChange={onSortingChange}
        showHeadersWhenEmpty={false}
        emptyDataElement={
          search || requesterFilterProps.value.length > 0 || dateFilter ? (
            <EmptySearchResults />
          ) : (
            <EmptyOrdersIndex />
          )
        }
        handleRowClick={handleRowClick}
        paginationChanged={paginationChanged}
        paginationSizeStored={pageSize}
        {...paginatedTableProps}
      />
      <DeleteOrderConfirmationDialog
        {...modalProps}
        onConfirmation={onDeleteOrderConfirmed}
        isLoading={isLoading}
      />
    </>
  );
};
